import React, { useEffect, useState } from 'react';
import { Property } from '../../domain/entities/Property';
import { TableViewTable } from './TableViewTable';
import { TableViewCard } from './TableViewCard';
import { useBreakpoint } from '../../../../shared/hooks/useBreakpoint';
import PublicMap from '../../../public-map/presentation/components/PublicMap';
import { TableViewCardSkeleton } from './skeleton/TableViewCardSkeleton';
import { TableViewTableSkeleton } from './skeleton/TableViewTableSkeleton';

export function TableView({
  mapKey,
  properties,
  isLoading,
  error,
}: {
  mapKey: string;
  properties: Property[];
  isLoading: boolean;
  error: Error | null;
}) {
  // lets get an active card, but you can always exit out of it (like in the split view)
  // HELEN TODO: Still need to do some responsive diesign work. in the next phase of this ticket!!!!
  //    className="h-[50vh] mdx:h-auto mdx:min-h-[50vh] w-full mdx:flex-1"
  //    'table-view-small-container': '800px',

  const [activeCardId, setActiveCardId] = useState<string | null>(
    properties[0]?.id
  );
  const { isXs } = useBreakpoint();

  // close active card
  const closeActiveCardId = () => setActiveCardId(null);
  useEffect(() => {
    if (properties.length > 0 && !isXs) {
      // Check if current activeCardId still exists in properties
      const activeExists = properties.some((p) => p.id === activeCardId);
      if (!activeExists) {
        setActiveCardId(properties[0].id);
      }
    }
  }, [properties, isXs]);

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    // Give the whole view a height; subtract your site header if needed
    <div id="table-view" className="flex flex-col md:flex-row min-h-0 h-full">
      {/* Mobile map should only show when there is no active card */}
      {!activeCardId && isXs && (
        <div
          id="table-view-map-mobile"
          className="min-h-[50vh] w-full flex-1 md:hidden"
        >
          <PublicMap mapKey={mapKey} />
        </div>
      )}

      {/* Left column: two rows (card + scrollable table) */}
      <div
        id="table-view-text-container"
        className="grid grid-rows-[auto,1fr] min-h-0 w-full max-w-[787px]"
      >
        {!isXs && isLoading && <TableViewCardSkeleton />}
        {activeCardId && (
          <div
            id="table-view-card-container"
            className="min-h-0 flex flex-col gap-3 relative border-b-2 border-black"
          >
            <button
              id="table-view-card-close"
              type="button"
              onClick={closeActiveCardId}
              className="absolute top-0 right-0 hover:bg-gray-200 p-1.5 leading-none "
              aria-label="Close"
            >
              ×
            </button>

            <TableViewCard key={activeCardId} id={activeCardId} />
          </div>
        )}

        {/* The table row gets all remaining space */}

        {isLoading ? (
          <TableViewTableSkeleton rowCount={10} />
        ) : (
          <TableViewTable
            properties={properties}
            activeId={activeCardId}
            onRowClick={(item) => setActiveCardId(item.id)}
          />
        )}
      </div>

      {/* Desktop map (shows when screen size is appropriate) */}
      {!isXs && (
        <div
          id="table-view-map-desktop"
          className="w-full flex-1 hidden md:block"
        >
          <PublicMap mapKey={mapKey} />
        </div>
      )}
    </div>
  );
}
